import { BIG_INITIALS_MAX_LENGTH } from "../../../../../api/src/shared/domain/hcp-registry";
import {
  registryFoundLabel,
  registryPresentation,
} from "../registryPresentation";
import { useTranslation } from "react-i18next";
import { cn } from "../../../lib/utils";
import { Input } from "../../../components/ui/Input";
import { NativeSelect } from "../../../components/ui/NativeSelect";
import { RegistryCoverage } from "../../../components/RegistryCoverage";
import Skeleton from "../../../components/skeleton/Skeleton";
import SkeletonTextLine from "../../../components/skeleton/SkeletonTextLine";
import type { Api } from "../../../services/api";

// The RPPS lookup result shapes, inferred from the public endpoint so the
// candidate fields stay in lockstep with the backend (no hand-written mirror).
type RegistryLookupBody =
  Api["post"]["/webapp/public/onboarding/registry-lookup"]["output"]["body"];
type DirectoryLookupBody =
  Api["post"]["/webapp/public/hcp-directory/search"]["output"]["body"];
type StandardCandidate = Extract<
  RegistryLookupBody,
  { status: "found" }
>["profile"];
type DirectoryCandidate =
  | Extract<DirectoryLookupBody, { status: "found" }>["profile"]
  | Extract<DirectoryLookupBody, { status: "page" }>["candidates"][number];
type HealthProCandidate = Extract<
  DirectoryCandidate,
  { registry: "health_pro" }
>;
type StpsCandidate = Extract<DirectoryCandidate, { registry: "stps" }>;
type BigCandidate = Extract<DirectoryCandidate, { registry: "big" }>;
type OmCandidate = Extract<DirectoryCandidate, { registry: "om" }>;
type RepsCandidate = Extract<DirectoryCandidate, { registry: "reps" }>;
export type RegistryCandidateView =
  | (StpsCandidate & { civility: null; organizationName: null; location: null })
  | StandardCandidate
  | (Omit<HealthProCandidate, "registry"> & {
      registry: "inami";
      civility: null;
      organizationName: null;
      location: string | null;
    })
  | (BigCandidate & {
      civility: null;
      organizationName: null;
      location: null;
    })
  | (OmCandidate & {
      civility: null;
      organizationName: null;
      location: string;
    })
  | (RepsCandidate & {
      civility: null;
      organizationName: null;
    });

// The frontend adds loading and query-editing states to directory results.
export type RegistryState =
  | { status: "loading" }
  | { status: "editing" }
  | { status: "unavailable" }
  | { status: "too_many_results" }
  | { status: "found"; profile: RegistryCandidateView }
  | {
      status: "page";
      candidates: RegistryCandidateView[];
      nextPage: number | null;
      loadState: "idle" | "loading" | "error";
    }
  | {
      status: "multiple";
      candidates: RegistryCandidateView[];
      truncated: boolean;
    }
  | { status: "not_found" }
  | { status: "inactive" };

function displayName(candidate: RegistryCandidateView): string {
  // The registry civility is a label ("Docteur") or code ("DR") — both map to
  // the "Dr." prefix; other civilities (Madame/Monsieur) get no prefix.
  const prefix =
    candidate.civility !== null && /^(dr|doct)/i.test(candidate.civility)
      ? "Dr. "
      : "";
  const givenName =
    candidate.registry === "big"
      ? candidate.initials === null
        ? ""
        : candidate.initials
      : candidate.registry === "om"
        ? ""
        : candidate.firstName === null
          ? ""
          : candidate.firstName;
  return `${prefix}${givenName} ${candidate.lastName}`.trim();
}

function initials(candidate: RegistryCandidateView): string {
  const first =
    candidate.registry === "big"
      ? candidate.initials === null
        ? ""
        : candidate.initials.charAt(0)
      : candidate.registry === "om"
        ? ""
        : candidate.firstName === null
          ? ""
          : candidate.firstName.charAt(0);
  const last = candidate.lastName.charAt(0);
  return `${first}${last}`.toUpperCase();
}

function repsSourceDetails(candidate: RepsCandidate): string[] {
  return [
    ...new Set([
      ...(candidate.professionLabel === null
        ? candidate.qualificationLabels.length > 0
          ? candidate.qualificationLabels
          : candidate.categoryLabels
        : []),
      ...(candidate.specialtyLabel === null ? candidate.specialtyLabels : []),
    ]),
  ];
}

function candidateLocations(candidate: RegistryCandidateView): string[] {
  if (candidate.registry === "reps") return candidate.locations;
  if (
    candidate.registry === "npi" ||
    candidate.registry === "inami" ||
    candidate.registry === "om"
  )
    return candidate.location === null ? [] : [candidate.location];
  return [];
}

function candidateDetails(candidate: RegistryCandidateView): string {
  const profession =
    candidate.registry === "stps" && candidate.professionLabel === null
      ? candidate.sourceProfessionLabel
      : candidate.professionLabel === null && candidate.registry === "big"
        ? candidate.professionCode
        : candidate.professionLabel;
  const specialty =
    candidate.specialtyLabel === null && candidate.registry === "big"
      ? candidate.specialtyCodes.join(", ")
      : candidate.specialtyLabel === null &&
          (candidate.registry === "om" || candidate.registry === "stps")
        ? candidate.qualificationLabels.join(", ")
        : candidate.specialtyLabel;
  return [
    ...new Set(
      [
        profession,
        specialty,
        ...(candidate.registry === "stps"
          ? [candidate.sourceProfessionLabel, ...candidate.qualificationLabels]
          : []),
        ...(candidate.registry === "reps" ? repsSourceDetails(candidate) : []),
        ...candidateLocations(candidate),
      ].filter((value) => value !== null && value !== "")
    ),
  ].join(" · ");
}

function ProfileCard({
  candidate,
  verifiedLabel,
}: {
  candidate: RegistryCandidateView;
  verifiedLabel: string;
}) {
  const { t } = useTranslation();
  const sourceDetails =
    candidate.registry === "reps"
      ? repsSourceDetails(candidate)
      : candidate.registry === "stps"
        ? [candidate.sourceProfessionLabel, ...candidate.qualificationLabels]
        : [];
  return (
    <div className="flex w-full flex-col gap-3 rounded-2xl border border-ds-black/6 bg-white px-4 py-6">
      <div className="flex items-center gap-3 border-b border-ds-slate-200 pb-2">
        <div className="flex size-13 shrink-0 items-center justify-center rounded-xl bg-ds-slate-200 font-dm-sans text-xl font-semibold text-ds-slate-500">
          {initials(candidate)}
        </div>
        <div className="flex flex-col gap-1.5">
          <p className="font-dm-sans text-ui-highlight font-semibold text-primary-black">
            {displayName(candidate)}
          </p>
          <p className="font-dm-sans text-ui-body text-ds-slate-400">
            {verifiedLabel}
          </p>
        </div>
      </div>
      <div className="flex items-center justify-between">
        {candidate.professionLabel !== null && (
          <div className="flex flex-col gap-1">
            <p className="font-dm-sans text-ui-caption font-medium text-ds-slate-400">
              {t("Profession")}
            </p>
            <p className="font-dm-sans text-ui-body font-medium text-primary-black">
              {candidate.professionLabel}
            </p>
          </div>
        )}
        {candidate.specialtyLabel !== null && (
          <div className="flex flex-1 flex-col gap-1 pl-4">
            <p className="font-dm-sans text-ui-caption font-medium text-ds-slate-400">
              {t("Specialty")}
            </p>
            <p className="font-dm-sans text-ui-body font-medium text-primary-black">
              {candidate.specialtyLabel}
            </p>
          </div>
        )}
      </div>
      {sourceDetails.length > 0 && (
        <p className="font-dm-sans text-ui-note text-ds-slate-600">
          {sourceDetails.join(" · ")}
        </p>
      )}
      {candidate.identifier !== null &&
        registryPresentation[candidate.registry].identifierLabel !== null && (
          <p className="text-ui-note text-ds-slate-600">
            {`${registryPresentation[candidate.registry].identifierLabel} ${candidate.identifier}`}
          </p>
        )}
      {candidateLocations(candidate).map((location) => (
        <p key={location} className="text-ui-note text-ds-slate-600">
          {location}
        </p>
      ))}
      {candidate.organizationName !== null && (
        <p className="font-dm-sans text-ui-note text-ds-slate-600">
          {candidate.organizationName}
        </p>
      )}
    </div>
  );
}

/**
 * The lookup wait, drawn as the card that is coming.
 *
 * Mirrors `ProfileCard` 1:1 — same container, same `size-13` tile, same DS
 * typography fed to each line — so the confirmed profile lands without moving a
 * pixel. The geometry comes from `ProfileCard` above and NOT from the Figma
 * skeleton frame, which is drawn at a 20px padding, a 48px tile and a
 * left-packed second column: honouring those numbers would make the card jump
 * on arrival, the one thing a silhouette exists to prevent. Only the bar widths
 * are the designer's.
 *
 * Both stat columns are always drawn, though the real card drops the one the
 * registry returned null. Nothing knows yet which it will be, and an absent
 * column reads as a shorter row rather than as a hole.
 */
function ProfileCardSkeleton(): React.JSX.Element {
  return (
    <div className="flex w-full flex-col gap-3 rounded-2xl border border-ds-black/6 bg-white px-4 py-6">
      {/* Header: size-13 tile + name (ui-highlight 18/26) over the verified
          line (ui-body 15/20), on the same divider the real card carries. */}
      <div className="flex items-center gap-3 border-b border-ds-slate-200 pb-2">
        <Skeleton width={52} height={52} className="shrink-0 rounded-xl" />
        <div className="flex flex-col gap-1.5">
          <SkeletonTextLine width={140} fontSize={18} lineHeight={26} />
          <SkeletonTextLine width={80} fontSize={15} lineHeight={20} />
        </div>
      </div>

      {/* Profession / Specialty: label (ui-caption 11/16) over value (ui-body). */}
      <div className="flex items-center justify-between">
        <div className="flex flex-col gap-1">
          <SkeletonTextLine width={50} fontSize={11} lineHeight={16} />
          <SkeletonTextLine width={90} fontSize={15} lineHeight={20} />
        </div>
        <div className="flex flex-1 flex-col gap-1 pl-4">
          <SkeletonTextLine width={50} fontSize={11} lineHeight={16} />
          <SkeletonTextLine width={90} fontSize={15} lineHeight={20} />
        </div>
      </div>

      {/* Employer (ui-note 13/20). */}
      <SkeletonTextLine width="100%" fontSize={13} lineHeight={20} />
    </div>
  );
}

function BigInitialsRefinement({
  registryInitials,
  onRegistryInitialsChange,
}: {
  registryInitials: string;
  onRegistryInitialsChange: (value: string) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-3">
      <label
        htmlFor="big-initials"
        className="font-dm-sans text-ui-note text-primary-black"
      >
        {t("Initials registered with BIG")}
      </label>
      <Input
        id="big-initials"
        value={registryInitials}
        autoComplete="off"
        maxLength={BIG_INITIALS_MAX_LENGTH}
        placeholder={t("For example, D.A.M.P.J.")}
        onChange={(event) => {
          onRegistryInitialsChange(event.target.value);
        }}
      />
    </div>
  );
}

function RepsSearchFields({
  firstName,
  firstSurname,
  secondSurname,
  onFirstNameChange,
  onFirstSurnameChange,
  onSecondSurnameChange,
}: {
  firstName: string;
  firstSurname: string;
  secondSurname: string;
  onFirstNameChange: (value: string) => void;
  onFirstSurnameChange: (value: string) => void;
  onSecondSurnameChange: (value: string) => void;
}) {
  const { t } = useTranslation();
  const tooLong =
    firstName.trim().length > 150 ||
    firstSurname.trim().length > 150 ||
    secondSurname.trim().length > 150;
  return (
    <div className="flex flex-col gap-2">
      <label className="flex flex-col gap-2 font-dm-sans text-ui-note text-primary-black">
        {t("First name in REPS")}
        <Input
          value={firstName}
          maxLength={150}
          onChange={(event) => {
            onFirstNameChange(event.target.value);
          }}
        />
      </label>
      <label className="flex flex-col gap-2 font-dm-sans text-ui-note text-primary-black">
        {t("First surname in REPS")}
        <Input
          value={firstSurname}
          maxLength={150}
          onChange={(event) => {
            onFirstSurnameChange(event.target.value);
          }}
        />
      </label>
      <label className="flex flex-col gap-2 font-dm-sans text-ui-note text-primary-black">
        {t("Second surname in REPS (optional)")}
        <Input
          value={secondSurname}
          maxLength={150}
          onChange={(event) => {
            onSecondSurnameChange(event.target.value);
          }}
        />
      </label>
      <p className="font-dm-sans text-ui-note text-ds-slate-500">
        {t("If your REPS record is missing, check the accents in your name.")}
      </p>
      {tooLong && (
        <p role="alert" className="font-dm-sans text-ui-note text-ds-slate-500">
          {t("Use at most 150 characters in each REPS name field.")}
        </p>
      )}
    </div>
  );
}

/**
 * Registry lookup content. The onboarding page owns its header and footer.
 * Selection can propose a profession and specialty for the following steps.
 */
type RegistryStepProps = {
  stpsSearch: {
    mode: "name" | "identifier";
    name: string;
    professionCode: string;
    professions: Extract<
      Api["get"]["/webapp/public/hcp-directory"]["output"]["body"]["directories"][number],
      { registry: "stps" }
    >["professions"];
    onNameChange: (value: string) => void;
    onProfessionChange: (value: string) => void;
    onModeChange: (mode: "name" | "identifier") => void;
  } | null;
  state: RegistryState;
  registryId: string;
  registry: RegistryCandidateView["registry"] | null;
  registryInitials: string;
  onRegistryIdChange: (value: string) => void;
  onRegistryInitialsChange: (value: string) => void;
  onSelectCandidate: (candidate: RegistryCandidateView) => void;
  onLoadMore: () => void;
  repsFirstName: string;
  repsFirstSurname: string;
  repsSecondSurname: string;
  repsResultsCurrent: boolean;
  onRepsFirstNameChange: (value: string) => void;
  onRepsFirstSurnameChange: (value: string) => void;
  onRepsSecondSurnameChange: (value: string) => void;
};

export function RegistryStep(props: RegistryStepProps) {
  const { t } = useTranslation();
  const stpsSearch = props.stpsSearch;
  return (
    <div className="flex w-full flex-col gap-3">
      {props.registry === "reps" && (
        <>
          <RepsSearchFields
            firstName={props.repsFirstName}
            firstSurname={props.repsFirstSurname}
            secondSurname={props.repsSecondSurname}
            onFirstNameChange={props.onRepsFirstNameChange}
            onFirstSurnameChange={props.onRepsFirstSurnameChange}
            onSecondSurnameChange={props.onRepsSecondSurnameChange}
          />
        </>
      )}
      {stpsSearch !== null && (
        <fieldset className="flex flex-col gap-3">
          <legend className="font-dm-sans text-ui-note text-primary-black">
            {t("Search by name or Autorisations-ID")}
          </legend>
          <div className="flex gap-4">
            <label className="flex items-center gap-2">
              <input
                type="radio"
                name="onboarding-stps-mode"
                checked={stpsSearch.mode === "name"}
                onChange={() => {
                  stpsSearch.onModeChange("name");
                }}
              />
              {t("By name")}
            </label>
            <label className="flex items-center gap-2">
              <input
                type="radio"
                name="onboarding-stps-mode"
                checked={stpsSearch.mode === "identifier"}
                onChange={() => {
                  stpsSearch.onModeChange("identifier");
                }}
              />
              {t("By Autorisations-ID")}
            </label>
          </div>
          {stpsSearch.mode === "name" ? (
            <>
              <label className="flex flex-col gap-2 font-dm-sans text-ui-note text-primary-black">
                {t("Name in STPS")}
                <Input
                  value={stpsSearch.name}
                  onChange={(event) => {
                    stpsSearch.onNameChange(event.target.value);
                  }}
                />
              </label>
              <label className="flex flex-col gap-2 font-dm-sans text-ui-note text-primary-black">
                {t("Profession")}
                <NativeSelect
                  value={stpsSearch.professionCode}
                  onChange={(event) => {
                    stpsSearch.onProfessionChange(event.target.value);
                  }}
                >
                  <option value="">{t("All professions")}</option>
                  {stpsSearch.professions.map((profession) => (
                    <option key={profession.code} value={profession.code}>
                      {profession.label}
                    </option>
                  ))}
                </NativeSelect>
              </label>
            </>
          ) : (
            <label className="flex flex-col gap-2 font-dm-sans text-ui-note text-primary-black">
              {t("Autorisations-ID")}
              <Input
                value={props.registryId}
                autoComplete="off"
                onChange={(event) => {
                  props.onRegistryIdChange(event.target.value);
                }}
              />
            </label>
          )}
        </fieldset>
      )}
      {props.registry === "reps" &&
        !props.repsResultsCurrent &&
        (props.state.status === "page" ||
          props.state.status === "multiple") && (
          <p
            role="status"
            className="font-dm-sans text-ui-note text-ds-slate-500"
          >
            {t("Search REPS again to update these results.")}
          </p>
        )}
      {props.registry === "big" && (
        <BigInitialsRefinement
          registryInitials={props.registryInitials}
          onRegistryInitialsChange={props.onRegistryInitialsChange}
        />
      )}
      <RegistryStepResults {...props} />
      {props.registry !== null && (
        <div className="font-dm-sans text-ui-note text-ds-slate-500">
          <RegistryCoverage registry={props.registry} />
        </div>
      )}
    </div>
  );
}

type RegistryStepResultProps = Pick<
  RegistryStepProps,
  | "state"
  | "registryId"
  | "registry"
  | "onRegistryIdChange"
  | "onSelectCandidate"
  | "onLoadMore"
  | "repsResultsCurrent"
>;

function RegistryStepResults({
  state,
  registryId,
  registry,
  onRegistryIdChange,
  onSelectCandidate,
  onLoadMore,
  repsResultsCurrent,
}: RegistryStepResultProps) {
  const { t } = useTranslation();

  if (state.status === "loading") {
    return <ProfileCardSkeleton />;
  }

  if (state.status === "found") {
    return (
      <div className="flex w-full flex-col gap-3">
        <ProfileCard
          candidate={state.profile}
          verifiedLabel={registryFoundLabel(state.profile.registry, t)}
        />
      </div>
    );
  }

  if (state.status === "multiple" || state.status === "page") {
    return (
      <div className="flex w-full flex-col gap-3">
        {registry !== "stps" &&
          state.status === "multiple" &&
          state.truncated && (
            <p>
              {t(
                "More profiles match this name. Enter your registry ID to find yours."
              )}
            </p>
          )}
        {registry !== "stps" &&
          state.status === "multiple" &&
          state.truncated && (
            <Input
              inputMode="numeric"
              value={registryId}
              placeholder={t("Enter your registry ID")}
              onChange={(event) => {
                onRegistryIdChange(event.target.value);
              }}
            />
          )}
        {state.candidates.map((candidate) => (
          <button
            key={
              candidate.registry === "inami" ||
              candidate.registry === "big" ||
              candidate.registry === "om" ||
              candidate.registry === "stps" ||
              candidate.registry === "reps"
                ? candidate.sourceRecordId
                : candidate.identifier
            }
            type="button"
            disabled={registry === "reps" && !repsResultsCurrent}
            className={cn(
              "flex w-full items-center gap-3 rounded-2xl border border-ds-black/6 bg-white px-4 py-3 text-left transition-colors",
              "active:bg-ds-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
            )}
            onClick={() => {
              onSelectCandidate(candidate);
            }}
          >
            <div className="flex size-10 shrink-0 items-center justify-center rounded-2xl bg-ds-slate-200 font-dm-sans text-sm font-semibold text-ds-slate-500">
              {initials(candidate)}
            </div>
            <div className="flex min-w-0 flex-col gap-0.5">
              <p className="truncate font-dm-sans text-ui-body font-medium text-primary-black">
                {displayName(candidate)}
              </p>
              <p
                className={cn(
                  "font-dm-sans text-ui-note text-ds-slate-500",
                  candidate.registry === "reps"
                    ? "whitespace-normal wrap-break-word"
                    : "truncate"
                )}
              >
                {candidateDetails(candidate)}
              </p>
              <p className="truncate font-dm-sans text-ui-caption text-ds-slate-400">
                {candidate.registry === "inami"
                  ? candidate.identifier === null
                    ? t("Health Pro record without an INAMI number")
                    : `INAMI ${candidate.identifier}`
                  : candidate.registry === "rpps"
                    ? `RPPS ${candidate.identifier}`
                    : candidate.registry === "reps"
                      ? t("REPS public record")
                      : `${registryPresentation[candidate.registry].identifierLabel} ${candidate.identifier}`}
              </p>
            </div>
          </button>
        ))}
        {state.status === "page" && state.nextPage !== null && (
          <button
            type="button"
            className="rounded-xl border border-ds-slate-300 bg-white p-3 font-dm-sans text-ui-body disabled:cursor-not-allowed disabled:opacity-50"
            disabled={
              state.loadState === "loading" ||
              (registry === "reps" && !repsResultsCurrent)
            }
            onClick={onLoadMore}
          >
            {state.loadState === "loading"
              ? t("Loading more profiles...")
              : state.loadState === "error"
                ? t("Retry loading profiles")
                : t("Load more profiles")}
          </button>
        )}
        {state.status === "page" && state.loadState === "error" && (
          <p role="alert">{t("Could not load more profiles.")}</p>
        )}
        {state.status === "page" &&
          registry !== "reps" &&
          registry !== "stps" && (
            <label className="flex flex-col gap-2 font-dm-sans text-ui-note text-primary-black">
              {t("Registry ID (optional)")}
              <Input
                inputMode="numeric"
                value={registryId}
                placeholder={t("Enter your registry ID")}
                onChange={(event) => {
                  onRegistryIdChange(event.target.value);
                }}
              />
            </label>
          )}
      </div>
    );
  }

  if (registry === "reps" || registry === "stps") return null;

  if (state.status === "editing" && registry !== "big")
    throw new Error("Invariant: unsupported directory search edit");

  if (
    state.status === "too_many_results" ||
    (registry === "big" &&
      (state.status === "not_found" || state.status === "editing"))
  ) {
    return (
      <div className="flex w-full flex-col gap-3">
        <label
          htmlFor="registry-id"
          className="font-dm-sans text-ui-note text-primary-black"
        >
          {t("Registry ID (optional)")}
        </label>
        <Input
          id="registry-id"
          inputMode="numeric"
          autoComplete="off"
          value={registryId}
          placeholder={t("Enter your registry ID")}
          onChange={(event) => {
            onRegistryIdChange(event.target.value);
          }}
        />
      </div>
    );
  }

  // The header explains the missing or unavailable record. The body offers
  // manual entry, which the footer submits or skips when left empty.
  return (
    <div className="flex w-full flex-col gap-1">
      <label className="font-dm-sans text-ui-note text-primary-black">
        {t("Registry ID (optional)")}
      </label>
      <Input
        variant="ghost"
        className="rounded-xl border-ds-slate-300 bg-white"
        inputMode="numeric"
        autoComplete="off"
        placeholder={t("Enter your registry ID")}
        value={registryId}
        onChange={(e) => {
          onRegistryIdChange(e.target.value);
        }}
      />
    </div>
  );
}
